/**
 * One place the rest of the app asks "where am I, and which way am I facing?".
 *
 * Real sensors when there are any; the keyboard walker when there are not. Nothing downstream
 * knows or cares which, so every mode is reachable on a desktop browser.
 */

import type { LatLng } from '@beeline/shared';

import { blendHeading, type HeadingBasis } from './heading-blend';
import { HeadingProvider, headingIsSupported, type HeadingReading } from './heading';
import { KeyboardWalker } from './keyboard';
import { LocationProvider, type Fix, type LocationError } from './location';

export type { Fix, LocationError } from './location';
export type { HeadingReading } from './heading';
export type { HeadingBasis } from './heading-blend';

export interface SensorState {
  fix: Fix | null;
  heading: number | null;
  /** False while the compass is unavailable — the caller falls back to the north-up view. */
  headingAvailable: boolean;
  /** True when the heading is a real compass bearing rather than relative rotation. */
  headingAbsolute: boolean;
  headingSource: HeadingReading['source'] | 'keyboard' | null;
  /**
   * Where the heading mostly came from once the two sources were blended.
   *
   * Kept separate from `headingSource`, which says which *sensor* answered. This says which
   * answer was believed, and they are different questions once the device is moving.
   */
  headingBasis: HeadingBasis;
  /**
   * True when the compass disagrees badly with the direction of travel.
   *
   * Almost always magnetic interference — a car, a speaker stack, a metal railing. The arrow is
   * still drawn, from the course rather than the compass, and the interface says the fix is
   * shakier than usual rather than quietly pretending everything is fine.
   */
  headingSuspect: boolean;
  locationError: LocationError | null;
  /** True when position and heading are being driven by the keyboard rather than sensors. */
  simulated: boolean;
}

export interface SensorHubOptions {
  onChange: (state: SensorState) => void;
  /** Turn on the keyboard walker even when sensors exist — used by the simulator. */
  forceKeyboard?: boolean;
  /** Where the keyboard walker starts if there is no fix at all. Defaults to central Athens. */
  keyboardOrigin?: LatLng;
}

/** Somewhere real, so a desktop session has plausible coordinates rather than null island. */
const DEFAULT_ORIGIN: LatLng = { lat: 37.9838, lng: 23.7275 };

/**
 * Is there plausibly a keyboard to fall back to?
 *
 * This matters more than it looks. On a phone whose compass is denied or missing there is no
 * keyboard, so a "keyboard heading" would sit at north forever — and an arrow drawn from a
 * heading that is always north is exactly the confident lie the product must never tell. On
 * such a device the right answer is no heading at all, which drops the user into the north-up
 * fallback (`ProductOverview.md` §4.7).
 */
function hasKeyboard(): boolean {
  if (typeof window.matchMedia !== 'function') return true;
  return !window.matchMedia('(hover: none) and (pointer: coarse)').matches;
}

export class SensorHub {
  private readonly location = new LocationProvider();
  private readonly heading = new HeadingProvider();
  private walker: KeyboardWalker | null = null;
  private readonly onChange: SensorHubOptions['onChange'];
  private readonly forceKeyboard: boolean;
  private readonly keyboardOrigin: LatLng;

  /**
   * Kept separately so the keyboard can never overwrite a real reading with a made-up one.
   * A real fix always wins unless the simulator has explicitly taken the wheel.
   */
  private realFix: Fix | null = null;
  private sensorHeading: HeadingReading | null = null;

  private state: SensorState = {
    fix: null,
    heading: null,
    headingAvailable: false,
    headingAbsolute: false,
    headingSource: null,
    headingBasis: 'compass',
    headingSuspect: false,
    locationError: null,
    simulated: false,
  };

  constructor(options: SensorHubOptions) {
    this.onChange = options.onChange;
    this.forceKeyboard = options.forceKeyboard ?? false;
    this.keyboardOrigin = options.keyboardOrigin ?? DEFAULT_ORIGIN;
  }

  get current(): SensorState {
    return this.state;
  }

  start(): void {
    this.location.start(
      (fix) => {
        this.realFix = fix;
        // Keep the walker where the body actually is, so that if GPS drops out mid-session,
        // walking carries on from the right place instead of teleporting across the city.
        if (!this.forceKeyboard) this.walker?.teleport({ lat: fix.lat, lng: fix.lng });
        this.publish({ locationError: null });
      },
      (error) => this.publish({ locationError: error }),
    );

    this.heading.start(
      (reading) => {
        this.sensorHeading = reading;
        this.publish({});
      },
      () => {
        // No compass. The north-up fallback takes over, and on a desktop the keyboard does.
        this.sensorHeading = null;
        this.startKeyboard();
        this.publish({});
      },
    );

    if (this.forceKeyboard || !headingIsSupported()) this.startKeyboard();
  }

  stop(): void {
    this.location.stop();
    this.heading.stop();
    this.walker?.stop();
    this.walker = null;
  }

  /** Ask for both permissions. Each must be called straight from its own tap. */
  requestLocation(): ReturnType<LocationProvider['requestOnce']> {
    return this.location.requestOnce();
  }

  requestHeading(): ReturnType<HeadingProvider['requestPermission']> {
    return this.heading.requestPermission();
  }

  private startKeyboard(): void {
    if (this.walker) return;
    // Never on a touch-only device unless the simulator explicitly asked for it.
    if (!this.forceKeyboard && !hasKeyboard()) return;

    const origin = this.realFix
      ? { lat: this.realFix.lat, lng: this.realFix.lng }
      : this.keyboardOrigin;

    this.walker = new KeyboardWalker({
      start: origin,
      heading: this.sensorHeading?.heading ?? 0,
      onChange: () => this.publish({}),
    });

    this.walker.start();
    // Publish the starting pose immediately, so a desktop session has something to draw.
    this.walker.teleport(origin);
  }

  /**
   * Merge the sources into one state, then notify.
   *
   * The ordering is the whole point: a real fix beats a made-up one, and a real compass beats
   * the keyboard, unless the simulator has explicitly taken over. Nothing here ever dresses a
   * simulated reading up as a measured one.
   */
  private publish(changes: Partial<SensorState>): void {
    const walker = this.walker?.state ?? null;
    const useKeyboardPosition = walker !== null && (this.forceKeyboard || this.realFix === null);
    const useKeyboardHeading =
      walker !== null && (this.forceKeyboard || this.sensorHeading === null);

    const fix: Fix | null = useKeyboardPosition
      ? {
          ...walker.position,
          // Exact by construction — but `simulated` below says where it came from, and the
          // debug panel shows that, so nothing is passing itself off as a measurement.
          accuracy: 5,
          at: Date.now(),
          confidence: 'good',
          usable: true,
          // Left null rather than invented. A keyboard has no altitude, and a made-up one would
          // put "40 m below you" on a screen that has no idea whether that is true.
          altitude: null,
          speed: null,
          course: null,
        }
      : this.realFix;

    /*
     * The heading the arrow actually uses.
     *
     * A keyboard walker has an exact heading and no GPS track, so it is taken as given. A real
     * device gets the blend: compass while standing still, course over ground once walking,
     * which is the answer to the biggest engineering risk the product named for itself.
     */
    const blended = useKeyboardHeading
      ? { heading: walker?.heading ?? null, basis: 'compass' as HeadingBasis, suspect: false }
      : blendHeading({
          compass: this.sensorHeading?.heading ?? null,
          course: fix?.course ?? null,
          speed: fix?.speed ?? null,
        });

    this.state = {
      ...this.state,
      ...changes,
      fix,
      heading: blended.heading,
      headingAvailable: blended.heading !== null,
      headingAbsolute: useKeyboardHeading
        ? true
        : // A heading derived from movement is absolute by construction: the GPS track is in
          // true-north terms whatever the magnetometer thinks.
          blended.basis === 'course' || (this.sensorHeading?.absolute ?? false),
      headingSource: useKeyboardHeading ? 'keyboard' : (this.sensorHeading?.source ?? null),
      headingBasis: blended.basis,
      headingSuspect: blended.suspect,
      simulated: useKeyboardPosition,
    };

    this.onChange(this.state);
  }
}
